// src/scenes/RestaurantDashboard/index.jsx
//
// Dashboard restaurant / hôtel — tout vient d'un SEUL appel : GET /restaurant/dashboard/
//
// Le backend masque lui-même les données de rentabilité (coût, bénéfice, marge,
// dépenses) pour un vendeur/caissier et l'annonce via `finances_visibles`.
// On s'aligne dessus plutôt que de deviner à partir du rôle stocké en local.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Avatar, Chip,
  CircularProgress, Alert, IconButton, Tooltip,
  Button, Tabs, Tab, Paper, LinearProgress, Divider,
} from '@mui/material';
// MUI v7 : le `Grid` par défaut a perdu les props `item` / `xs` / `md` au profit
// de `size={{...}}`. Tout l'admin est écrit avec l'ancienne API → on importe la
// version historique, qui les accepte toujours.
import { GridLegacy as Grid } from '@mui/material';
import {
  Restaurant, RoomService, AttachMoney, ShoppingCart, TrendingUp,
  Refresh, Hotel, Receipt, Today, Insights, Percent,
  Fastfood, LocalBar, MoneyOff, AccountBalanceWallet,
} from '@mui/icons-material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Legend,
  Tooltip as RechartsTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar,
} from 'recharts';
import api from '../../services/auth';

// ─── Utilitaires ─────────────────────────────────────────────────────────────
const fmt = (n) => n == null ? '—' : new Intl.NumberFormat('fr-HT', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}).format(n) + ' HTG';

const fmtNum = (n) => n == null ? '—' : new Intl.NumberFormat('fr-HT').format(n);

const fmtPct = (n) => n == null ? '—' : `${Number(n).toFixed(1)} %`;

const fmtTime = (dateStr) => {
  if (!dateStr) return '—';
  return new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' })
    .format(new Date(dateStr));
};

const fmtDate = (d) => new Intl.DateTimeFormat('fr-FR', {
  weekday: 'short',
  day: '2-digit',
  month: 'short',
}).format(new Date(d));

const COLORS_PIE = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

const COULEUR_STATUT = {
  ouverte: 'warning',
  en_preparation: 'info',
  servie: 'success',
};
const LIBELLE_STATUT = {
  ouverte: 'Ouverte',
  en_preparation: 'En préparation',
  servie: 'Prête à payer',
};

// Une marge saine est verte, une marge faible orange, une marge négative rouge.
const couleurMarge = (marge) => {
  if (marge == null) return 'text.secondary';
  if (marge < 0) return 'error.main';
  if (marge < 20) return 'warning.main';
  return 'success.main';
};

// ─── Briques ─────────────────────────────────────────────────────────────────

function KpiCard({ icon, label, value, sousTitre, color = '#3b82f6', alert = false }) {
  return (
    <Card sx={{
      height: '100%',
      border: alert ? '1px solid #ef4444' : 'none',
      bgcolor: alert ? 'rgba(239,68,68,0.06)' : 'background.paper',
    }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box minWidth={0}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
              {label}
            </Typography>
            <Typography fontWeight={800} color={alert ? 'error.main' : 'text.primary'} sx={{ fontSize: '1.1rem' }} noWrap>
              {value}
            </Typography>
            {sousTitre && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }} noWrap>
                {sousTitre}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: `${color}1a`, width: 32, height: 32 }}>
            {React.cloneElement(icon, { sx: { color, fontSize: 18 } })}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
}

function StatsCard({ label, value, color = 'primary' }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: 'action.hover' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography fontWeight={800} color={`${color}.main`} sx={{ fontSize: '1.1rem' }}>
        {value}
      </Typography>
    </Paper>
  );
}

function TitreSection({ icon, children, extra }) {
  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
      <Box display="flex" alignItems="center" gap={1}>
        {React.cloneElement(icon, { sx: { fontSize: 20 } })}
        <Typography fontWeight={700}>{children}</Typography>
      </Box>
      {extra}
    </Box>
  );
}

function EtatVide({ children }) {
  return (
    <Typography color="text.secondary" textAlign="center" py={3} variant="body2">
      {children}
    </Typography>
  );
}

// ─── Rentabilité d'une période (CA → coût → bénéfice → marge) ────────────────
function BlocRentabilite({ titre, data }) {
  if (!data) return null;
  const marge = data.marge_pourcent;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="caption" color="text.secondary" textTransform="uppercase" letterSpacing={0.8}>
        {titre}
      </Typography>

      <Box display="flex" justifyContent="space-between" alignItems="baseline" mt={1}>
        <Typography variant="body2" color="text.secondary">Chiffre d'affaires</Typography>
        <Typography fontWeight={700}>{fmt(data.chiffre_affaires)}</Typography>
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="baseline" mt={0.5}>
        <Typography variant="body2" color="text.secondary">Coût de revient</Typography>
        <Typography fontWeight={600} color="error.main">− {fmt(data.cout_revient)}</Typography>
      </Box>

      <Divider sx={{ my: 1 }} />

      <Box display="flex" justifyContent="space-between" alignItems="baseline">
        <Typography fontWeight={700}>Bénéfice</Typography>
        <Typography fontWeight={800} color={couleurMarge(marge)} sx={{ fontSize: '1.15rem' }}>
          {fmt(data.benefice)}
        </Typography>
      </Box>

      <Box display="flex" alignItems="center" gap={1} mt={1}>
        <LinearProgress
          variant="determinate"
          // La barre plafonne à 100 % : au-delà elle n'apporte plus d'info
          value={Math.max(0, Math.min(100, Number(marge) || 0))}
          sx={{ flex: 1, height: 6, borderRadius: 3 }}
          color={marge < 0 ? 'error' : marge < 20 ? 'warning' : 'success'}
        />
        <Typography variant="caption" fontWeight={700} color={couleurMarge(marge)}>
          {fmtPct(marge)}
        </Typography>
      </Box>
    </Paper>
  );
}

// ─── Évolution 7 jours (CA + bénéfice) ───────────────────────────────────────
function EvolutionSection({ data, finances }) {
  if (!data?.evolution_7_jours?.length) return null;

  const evoData = data.evolution_7_jours.map((item) => ({
    ...item,
    dateFormatted: fmtDate(item.date),
  }));

  return (
    <Card sx={{ width: '100%' }}>
      <CardContent sx={{ py: 2 }}>
        <TitreSection icon={<TrendingUp sx={{ color: '#3b82f6' }} />}>
          Évolution sur 7 jours
        </TitreSection>

        <Box sx={{ height: 320, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={evoData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.15)" />
              <XAxis dataKey="dateFormatted" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
              <RechartsTooltip formatter={(v) => fmt(v)} />
              <Legend />
              <Line
                type="monotone"
                dataKey="chiffre_affaires"
                stroke="#3b82f6"
                strokeWidth={3}
                dot={{ r: 4, fill: '#3b82f6' }}
                name="Chiffre d'affaires"
              />
              {finances && (
                <Line
                  type="monotone"
                  dataKey="benefice"
                  stroke="#10b981"
                  strokeWidth={2}
                  strokeDasharray="5 4"
                  dot={{ r: 3, fill: '#10b981' }}
                  name="Bénéfice"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </Box>

        <Box display="flex" justifyContent="space-between" mt={2} pt={1}>
          <Box>
            <Typography variant="caption" color="text.secondary">CA du mois</Typography>
            <Typography fontWeight={800} color="success.main">{fmt(data.chiffre_affaires)}</Typography>
          </Box>
          {finances && (
            <Box textAlign="center">
              <Typography variant="caption" color="text.secondary">Bénéfice du mois</Typography>
              <Typography fontWeight={800} color={couleurMarge(data.marge_pourcent)}>
                {fmt(data.benefice)}
              </Typography>
            </Box>
          )}
          <Box textAlign="right">
            <Typography variant="caption" color="text.secondary">Commandes</Typography>
            <Typography fontWeight={800}>{fmtNum(data.nombre_commandes)}</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Aujourd'hui ─────────────────────────────────────────────────────────────
function AujourdHuiSection({ data, finances }) {
  if (!data) return null;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ py: 2 }}>
        <TitreSection
          icon={<Today />}
          extra={finances && (
            <Chip
              size="small"
              label={`Marge ${fmtPct(data.marge_pourcent)}`}
              color={data.marge_pourcent < 0 ? 'error' : data.marge_pourcent < 20 ? 'warning' : 'success'}
            />
          )}
        >
          Aujourd'hui
        </TitreSection>

        <Grid container spacing={1} mb={2}>
          <Grid item xs={6} sm={3}>
            <StatsCard label="CA" value={fmt(data.chiffre_affaires)} color="success" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatsCard label="Commandes" value={fmtNum(data.nombre_commandes_payees)} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatsCard label="Panier moyen" value={fmt(data.panier_moyen)} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatsCard label="Remises" value={fmt(data.total_remises)} color="warning" />
          </Grid>
          {finances && (
            <>
              <Grid item xs={6} sm={3}>
                <StatsCard label="Coût de revient" value={fmt(data.cout_revient)} color="error" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatsCard label="Bénéfice" value={fmt(data.benefice)} color="success" />
              </Grid>
            </>
          )}
          <Grid item xs={6} sm={3}>
            <StatsCard label="Annulées" value={fmtNum(data.nombre_commandes_annulees)} color="error" />
          </Grid>
        </Grid>

        <Grid container spacing={1.5}>
          {data.par_methode_paiement?.length > 0 && (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Moyens de paiement</Typography>
              <Box sx={{ height: 170 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.par_methode_paiement}
                      cx="50%" cy="50%" innerRadius={30} outerRadius={55}
                      dataKey="total" nameKey="methode_paiement"
                    >
                      {data.par_methode_paiement.map((_, i) => (
                        <Cell key={i} fill={COLORS_PIE[i % COLORS_PIE.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(v) => fmt(v)} />
                    <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Grid>
          )}

          {data.par_type_commande?.length > 0 && (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Type de commande</Typography>
              <Box sx={{ height: 170 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.par_type_commande}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.15)" />
                    <XAxis dataKey="type_commande" tick={{ fontSize: 10 }} />
                    <YAxis hide />
                    <RechartsTooltip formatter={(v) => fmtNum(v)} />
                    <Bar dataKey="count" fill="#3b82f6" name="Commandes" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
}

// ─── Commandes en cours (temps réel) ─────────────────────────────────────────
function TempsReelSection({ data }) {
  const commandes = data?.detail_commandes || [];

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ py: 2 }}>
        <TitreSection
          icon={<RoomService />}
          extra={<Chip label={fmtNum(data?.commandes_ouvertes ?? 0)} size="small" color="warning" />}
        >
          Commandes en cours
        </TitreSection>

        {commandes.length === 0 ? (
          <EtatVide>Aucune commande en cours</EtatVide>
        ) : (
          <Box display="flex" flexDirection="column" gap={1}>
            {commandes.slice(0, 8).map((cmd) => (
              <Paper
                key={cmd.id}
                variant="outlined"
                sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
              >
                <Box minWidth={0}>
                  <Typography fontWeight={600} variant="body2" noWrap>
                    {cmd.nom_client || cmd.numero}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtTime(cmd.date_commande)} · {fmt(cmd.montant_total)}
                  </Typography>
                </Box>
                <Chip
                  label={LIBELLE_STATUT[cmd.statut] || cmd.statut}
                  size="small"
                  color={COULEUR_STATUT[cmd.statut] || 'default'}
                />
              </Paper>
            ))}
            {commandes.length > 8 && (
              <Typography variant="caption" color="text.secondary" textAlign="center">
                +{commandes.length - 8} autres
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Top ventes (une liste réutilisée pour plats et boissons) ────────────────
function TopListe({ icon, titre, rows, cleNom, finances }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ py: 2 }}>
        <TitreSection icon={icon}>{titre}</TitreSection>

        {!rows?.length ? (
          <EtatVide>Aucune vente ce mois-ci</EtatVide>
        ) : (
          <Box>
            <Box display="flex" justifyContent="space-between" pb={0.5}>
              <Typography variant="caption" color="text.secondary">Article</Typography>
              <Box display="flex" gap={2}>
                <Typography variant="caption" color="text.secondary" sx={{ width: 40, textAlign: 'right' }}>Qté</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ width: 80, textAlign: 'right' }}>CA</Typography>
                {finances && (
                  <Typography variant="caption" color="text.secondary" sx={{ width: 80, textAlign: 'right' }}>Bénéfice</Typography>
                )}
              </Box>
            </Box>

            {rows.map((row, i) => (
              <Box
                key={i}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                py={0.75}
                sx={{ borderTop: '1px solid', borderColor: 'divider' }}
              >
                <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                  <Typography fontWeight={700} color="primary" variant="body2">{i + 1}.</Typography>
                  <Typography variant="body2" noWrap>{row[cleNom]}</Typography>
                </Box>
                <Box display="flex" gap={2} alignItems="center" flexShrink={0}>
                  <Typography variant="body2" sx={{ width: 40, textAlign: 'right' }}>
                    {fmtNum(row.quantite_vendue)}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ width: 80, textAlign: 'right' }}>
                    {fmt(row.chiffre_affaires)}
                  </Typography>
                  {finances && (
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={row.benefice < 0 ? 'error.main' : 'success.main'}
                      sx={{ width: 80, textAlign: 'right' }}
                    >
                      {fmt(row.benefice)}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Hôtel ───────────────────────────────────────────────────────────────────
function HotelSection({ data }) {
  if (!data) return null;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ py: 2 }}>
        <TitreSection
          icon={<Hotel />}
          extra={<Chip size="small" label={`${fmtPct(data.taux_occupation_pourcent)} occupé`} color="info" />}
        >
          Hôtel
        </TitreSection>

        <Grid container spacing={1} mb={2}>
          <Grid item xs={4}><StatsCard label="Total" value={fmtNum(data.total_chambres)} /></Grid>
          <Grid item xs={4}><StatsCard label="Occupées" value={fmtNum(data.chambres_occupees)} color="warning" /></Grid>
          <Grid item xs={4}><StatsCard label="Libres" value={fmtNum(data.chambres_disponibles)} color="success" /></Grid>
        </Grid>

        <LinearProgress
          variant="determinate"
          value={Math.max(0, Math.min(100, Number(data.taux_occupation_pourcent) || 0))}
          sx={{ height: 6, borderRadius: 3, mb: 2 }}
          color="info"
        />

        <Grid container spacing={1}>
          <Grid item xs={6}>
            <StatsCard label="Revenus aujourd'hui" value={fmt(data.revenus_aujourd_hui)} color="primary" />
          </Grid>
          <Grid item xs={6}>
            <StatsCard label="Revenus ce mois" value={fmt(data.revenus_ce_mois)} color="primary" />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

function LocationsEnCoursSection({ locations }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ py: 2 }}>
        <TitreSection
          icon={<Hotel />}
          extra={<Chip label={fmtNum(locations?.length ?? 0)} size="small" color="warning" />}
        >
          Chambres occupées
        </TitreSection>

        {!locations?.length ? (
          <EtatVide>Aucune chambre occupée</EtatVide>
        ) : (
          <Box display="flex" flexDirection="column" gap={1}>
            {locations.map((loc) => (
              <Paper
                key={loc.id}
                variant="outlined"
                sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
              >
                <Box minWidth={0}>
                  <Typography fontWeight={600} variant="body2" noWrap>
                    Chambre {loc.chambre__numero}
                    {loc.nom_client ? ` — ${loc.nom_client}` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Depuis {fmtTime(loc.heure_debut)}
                  </Typography>
                </Box>
                <Box textAlign="right" flexShrink={0}>
                  <Chip label={loc.type_location} size="small" sx={{ textTransform: 'capitalize' }} />
                  <Typography variant="caption" display="block" fontWeight={600}>
                    {fmt(loc.prix_convenu)}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Dépenses ────────────────────────────────────────────────────────────────
function DepensesSection({ data }) {
  if (!data) return null;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ py: 2 }}>
        <TitreSection icon={<Receipt />}>Dépenses</TitreSection>

        <Grid container spacing={1} mb={2}>
          <Grid item xs={6}><StatsCard label="Aujourd'hui" value={fmt(data.aujourd_hui)} color="error" /></Grid>
          <Grid item xs={6}><StatsCard label="Ce mois" value={fmt(data.ce_mois)} color="error" /></Grid>
        </Grid>

        {!data.par_type_ce_mois?.length ? (
          <EtatVide>Aucune dépense enregistrée ce mois-ci</EtatVide>
        ) : (
          <>
            <Box sx={{ height: 180 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.par_type_ce_mois} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.15)" />
                  <XAxis type="number" tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
                  <YAxis type="category" dataKey="type_depense" width={90} tick={{ fontSize: 11 }} />
                  <RechartsTooltip formatter={(v) => fmt(v)} />
                  <Bar dataKey="total" fill="#ef4444" name="Total" />
                </BarChart>
              </ResponsiveContainer>
            </Box>

            {data.par_type_ce_mois.map((dep, i) => (
              <Box key={i} display="flex" justifyContent="space-between" py={0.5}>
                <Typography variant="caption" textTransform="capitalize">
                  {dep.type_depense} ({dep.count})
                </Typography>
                <Typography variant="caption" fontWeight={600}>{fmt(dep.total)}</Typography>
              </Box>
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function RestaurantDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [userName, setUserName] = useState('');

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('restaurant/dashboard/');
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError("Vous n'avez pas accès au module restaurant.");
      } else {
        setError(err.response?.data?.error || 'Erreur lors du chargement du tableau de bord');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setUserName(user.first_name || user.username || '');
    } catch (_) { /* localStorage illisible : on affiche un accueil générique */ }
    fetchDashboard();
  }, [fetchDashboard]);

  // Le serveur décide de ce qui est visible ; par défaut on masque.
  const finances = data?.finances_visibles === true;

  // Bénéfice net = bénéfice des ventes − dépenses du mois. Le backend ne le
  // calcule pas : il expose les deux termes séparément.
  const beneficeNetMois = useMemo(() => {
    if (!finances || !data) return null;
    const brut = Number(data.ce_mois?.benefice) || 0;
    const dep = Number(data.depenses?.ce_mois) || 0;
    return brut - dep;
  }, [finances, data]);

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={50} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="contained" onClick={fetchDashboard} startIcon={<Refresh />}>
          Réessayer
        </Button>
      </Box>
    );
  }

  const onglets = [
    { label: 'Restaurant', icon: <Restaurant /> },
    ...(finances ? [{ label: 'Rentabilité', icon: <Insights /> }] : []),
    { label: 'Hôtel', icon: <Hotel /> },
    ...(finances ? [{ label: 'Dépenses', icon: <Receipt /> }] : []),
  ];
  // Le nombre d'onglets dépend de `finances_visibles` : on borne l'index pour
  // ne pas se retrouver sur un onglet inexistant après un rafraîchissement.
  const indexActif = Math.min(tabValue, onglets.length - 1);
  const ongletActif = onglets[indexActif]?.label;

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography fontWeight={800} sx={{ fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
          Bienvenue, {userName || 'utilisateur'} 👋
        </Typography>
        <Tooltip title="Rafraîchir">
          <IconButton onClick={fetchDashboard} disabled={loading} size="small">
            {loading ? <CircularProgress size={20} /> : <Refresh />}
          </IconButton>
        </Tooltip>
      </Box>

      {!finances && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Les données de rentabilité (coût, bénéfice, marge, dépenses) sont réservées
          au propriétaire et au manager.
        </Alert>
      )}

      {/* KPIs du jour */}
      <Grid container spacing={1.5} mb={2}>
        <Grid item xs={6} sm={3}>
          <KpiCard
            icon={<AttachMoney />}
            label="CA aujourd'hui"
            value={fmt(data?.aujourd_hui?.chiffre_affaires)}
            color="#10b981"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          {finances ? (
            <KpiCard
              icon={<AccountBalanceWallet />}
              label="Bénéfice aujourd'hui"
              value={fmt(data?.aujourd_hui?.benefice)}
              sousTitre={`Marge ${fmtPct(data?.aujourd_hui?.marge_pourcent)}`}
              color="#22c55e"
            />
          ) : (
            <KpiCard
              icon={<ShoppingCart />}
              label="Panier moyen"
              value={fmt(data?.aujourd_hui?.panier_moyen)}
              color="#22c55e"
            />
          )}
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard
            icon={<ShoppingCart />}
            label="Commandes payées"
            value={fmtNum(data?.aujourd_hui?.nombre_commandes_payees)}
            color="#3b82f6"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard
            icon={<RoomService />}
            label="Commandes en cours"
            value={fmtNum(data?.temps_reel?.commandes_ouvertes)}
            sousTitre={`${fmtNum(data?.temps_reel?.commandes_a_emporter)} à emporter`}
            color="#8b5cf6"
            alert={data?.temps_reel?.commandes_ouvertes > 0}
          />
        </Grid>
      </Grid>

      <Tabs
        value={indexActif}
        onChange={(_, v) => setTabValue(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, minHeight: 36 }}
      >
        {onglets.map((o) => (
          <Tab key={o.label} icon={o.icon} label={o.label} sx={{ minHeight: 36, py: 0 }} />
        ))}
      </Tabs>

      {ongletActif === 'Restaurant' && (
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={8}>
            <AujourdHuiSection data={data?.aujourd_hui} finances={finances} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TempsReelSection data={data?.temps_reel} />
          </Grid>
          <Grid item xs={12}>
            <EvolutionSection data={data?.ce_mois} finances={finances} />
          </Grid>
        </Grid>
      )}

      {ongletActif === 'Rentabilité' && (
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={4}>
            <BlocRentabilite titre="Aujourd'hui" data={data?.aujourd_hui} />
          </Grid>
          <Grid item xs={12} md={4}>
            <BlocRentabilite titre="Ce mois-ci" data={data?.ce_mois} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="caption" color="text.secondary" textTransform="uppercase" letterSpacing={0.8}>
                Résultat du mois
              </Typography>
              <Box display="flex" justifyContent="space-between" mt={1}>
                <Typography variant="body2" color="text.secondary">Bénéfice des ventes</Typography>
                <Typography fontWeight={600}>{fmt(data?.ce_mois?.benefice)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mt={0.5}>
                <Typography variant="body2" color="text.secondary">Dépenses</Typography>
                <Typography fontWeight={600} color="error.main">− {fmt(data?.depenses?.ce_mois)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between" alignItems="baseline">
                <Typography fontWeight={700}>Net</Typography>
                <Typography
                  fontWeight={800}
                  sx={{ fontSize: '1.15rem' }}
                  color={beneficeNetMois < 0 ? 'error.main' : 'success.main'}
                >
                  {fmt(beneficeNetMois)}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                Ventes du mois, hors revenus de l'hôtel.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <TopListe
              icon={<Fastfood />}
              titre="Top plats du mois"
              rows={data?.top_ventes?.top_plats_ce_mois}
              cleNom="plat__nom"
              finances={finances}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TopListe
              icon={<LocalBar />}
              titre="Top boissons du mois"
              rows={data?.top_ventes?.top_boissons_ce_mois}
              cleNom="boisson__nom"
              finances={finances}
            />
          </Grid>
        </Grid>
      )}

      {ongletActif === 'Hôtel' && (
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <HotelSection data={data?.hotel} />
          </Grid>
          <Grid item xs={12} md={6}>
            <LocationsEnCoursSection locations={data?.hotel?.locations_en_cours} />
          </Grid>
        </Grid>
      )}

      {ongletActif === 'Dépenses' && (
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <DepensesSection data={data?.depenses} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ py: 2 }}>
                <TitreSection icon={<MoneyOff />}>Poids des dépenses</TitreSection>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <StatsCard label="CA du mois" value={fmt(data?.ce_mois?.chiffre_affaires)} color="success" />
                  </Grid>
                  <Grid item xs={6}>
                    <StatsCard label="Dépenses du mois" value={fmt(data?.depenses?.ce_mois)} color="error" />
                  </Grid>
                </Grid>
                <Box display="flex" alignItems="center" gap={1} mt={2}>
                  <Percent sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Les dépenses représentent{' '}
                    <strong>
                      {data?.ce_mois?.chiffre_affaires > 0
                        ? fmtPct((Number(data.depenses?.ce_mois) || 0) / data.ce_mois.chiffre_affaires * 100)
                        : '—'}
                    </strong>{' '}
                    du chiffre d'affaires du mois.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
